import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import PasswordRequirements, { isPasswordStrong } from "@/components/PasswordRequirements";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Reset link is missing or invalid.");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(password)) { setError("Please make your password stronger — see the requirements below."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    setError("");
    try {
      await resetPassword(token, password);
      navigate("/login?reset_ok=1", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Set a new password." subtitle="Reset password">
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="reset-form">
        <div>
          <label className="ticker-label !text-[10px] block mb-1.5">New password</label>
          <input
            type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password"
            autoComplete="new-password"
            className="w-full px-4 py-2.5 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6]"
          />
          <PasswordRequirements value={password} />
        </div>
        <div>
          <label className="ticker-label !text-[10px] block mb-1.5">Confirm password</label>
          <input
            type="password" required value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-confirm"
            autoComplete="new-password"
            className="w-full px-4 py-2.5 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6]"
          />
        </div>
        {error && <div data-testid="reset-error" className="text-sm text-red-600">{error}</div>}
        <button
          type="submit" disabled={busy || !token}
          data-testid="reset-submit"
          className="w-full inline-flex items-center justify-center gap-2 mt-2 px-5 py-3 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border-2 border-black text-sm tracking-[0.18em] uppercase font-bold disabled:opacity-50"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Save new password
        </button>
        <div className="mt-4 text-sm text-center">
          <Link to="/login" className="underline">Back to sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
}
